import React, {Component} from "react";
import {Divider} from "antd";
import * as PropTypes from "prop-types";
import * as R from "ramda";
import {getItem, ItemSmall} from "./Boilerplate/ItemSmall";
import "./CharacterSummary.less";
import {colors} from "./Boilerplate/ItemInfo";
import {itemImageUrl, SlotSelection} from "./Boilerplate/ItemSlot";
import "./CharacterBuilder.less"

const statOrder = [
  "damage",
  "armor",
  "hp",
  "hpregen",
  "mp",
  "mpregen",
  "mainstat",
  "str",
  "agi",
  "int",
  "allstat",
  "movespeed",
  "movespeedpercent",
  "aadamagepercent",
  "affinitydarkpercent",
  "affinityearthpercent",
  "affinityflamepercent",
  "affinitylightpercent",
  "affinityiwpercent",
  "affinitywlpercent",
  "attackspeedpercent",
  "critchancepercent",
  "critmultiplier",
  "dodgechancepercent",
  "drpercent",
  "dt",
  "dtpercent",
  "expgainpercent",
  "healingpercent",
  "healreceivedpercent",
  "mdpercent",
  "periodicdamagepercent",
  "revivaltimepercent",
  "skilldamagepercent",
  "passive",
  "active",
  "spec",
];

const statNames = {
  "active": "Active",
  "affinitydarkpercent": "Dark Affinity",
  "affinityearthpercent": "Earth Affinity",
  "affinityflamepercent": "Fire Affinity",
  "affinityiwpercent": "Ice/Water Affinity",
  "affinitylightpercent": "Light Affinity",
  "affinitywlpercent": "Wind/Lightning Affinity",
  "agi": "Agility",
  "allstat": "All Stats",
  "armor": "Armor",
  "aadamagepercent": "Auto-attack Damage",
  "attackspeedpercent": "Attack Speed",
  "critchancepercent": "Critical Chance",
  "critmultiplier": "Critical Multiplier",
  "damage": "Damage",
  "dodgechancepercent": "Dodge Chance",
  "drpercent": "Damage Reduction",
  "dt": "Damage Reduction",
  "dtpercent": "Damage Taken",
  "expgainpercent": "Experience Gain",
  "healingpercent": "Healing",
  "healreceivedpercent": "Healing Received",
  "hp": "Health",
  "hpregen": "Health Regeneration",
  "int": "Intelligence",
  "mainstat": "Main Stat",
  "mdpercent": "Magic Defense",
  "movespeed": "Movement Speed",
  "movespeedpercent": "Movement Speed",
  "mp": "Mana",
  "mpregen": "Mana Regeneration",
  "passive": "Passive",
  "periodicdamagepercent": "Periodic Damage",
  "revivaltimepercent": "Revival Time",
  "skilldamagepercent": "Skill Damage",
  "spec": "Specialty",
  "str": "Strength",
};

const cleanStats = _stats => {
  const stats = Object.assign({}, _stats);
  if (stats.active && typeof(stats.active) === "string") stats.active=[stats.active];
  return stats;
};

const diminishingReturnsMultiplier = (a, b) => 1 - ((1-a) * (1-b));

const reducer = (key, a, b) => {
  return ['active', 'passive', 'spec'].includes(key) ? R.concat(a, b) :
    ['mdpercent', 'drpercent'].includes(key) ? diminishingReturnsMultiplier(a, b) :
    ['movespeed'].includes(key) ? R.max(a, b) :
      ((a * 10000) + (b * 10000)) / 10000;
};

const combineStats = (_a, _b) => {
  const [a, b] = [_a, _b].map(cleanStats);
  const result = R.mergeWithKey(reducer, a, b);
  Object.keys(result).forEach(k => result[k] === null && delete result[k]);
  return result;
};

const cleanFloat = v => parseFloat((v * 100).toFixed(2));

export class StatDisplay extends Component {
  render() {
    const {stat, value, colored} = this.props;
    const style = colored && {color: (statOrder.slice(0, 10).includes(stat) ? colors.orange : colors.cyan)};
    if (['active', 'passive', 'spec'].includes(stat))
      return <div style={style}><em>{statNames[stat]}</em>: <ul className='image-bullet'>{value.map(v =>
        Array.isArray(v) ? <li style={{backgroundImage: itemImageUrl(v[0])}}>{v[1]}</li> : <li>{v}</li>
      )}</ul></div>;
    if (stat.includes('percent'))
      return <div style={style}>{value > 0 && '+'}{cleanFloat(value)}% {statNames[stat]}</div>;
    if (stat.includes('multiplier'))
      return <div style={style}>{value > 0 && '+'}{value}x {statNames[stat]}</div>;
    return <div style={style}>{value > 0 && '+'}{value} {statNames[stat]}</div>;
  }
}

export class StatsDisplay extends Component {
  render() {
    const {stats, colored} = this.props;
    if (!stats) return null;
    return <React.Fragment>
      {statOrder.map(key => stats[key] && <StatDisplay stat={key} value={stats[key]} colored={colored}/>)}
    </React.Fragment>
  }
}

export const SlotNames = {
  head: 'Head',
  body: 'Body',
  weapon: 'Weapon',
  wing: 'Wings',
  ring: 'Accessory',
};

export const EquipmentSummary = props => <><Divider style={{marginTop: 0}}>Equipped</Divider>
  {["head", "body", "weapon", "wing", "ring"].map(slot =>
  <div>{SlotNames[slot]}: <SlotSelection
    type={props.character[slot]} slot={slot} item={props.equipped[slot]}
    handleSelectItem={props.handleSelectItem}><span className="anchor">{props.equipped[slot] ?
    <ItemSmall inline name={props.equipped[slot]}/> : "Not Selected"}</span></SlotSelection></div>)}</>;

EquipmentSummary.propTypes = {
  equipped: PropTypes.any,
  character: PropTypes.any,
  handleSelectItem: PropTypes.func,
};

const withName = name => list => list.map(v => [name, v]);
const mapStats = item => R.evolve({
  passive: withName(item.name),
  active: withName(item.name),
  spec: withName(item.name),
}, item.stats);

export const StatsSummary = props => {
  const equipped_items = Object.values(props.equipped).map(getItem).flat();
  const equipped_stats = equipped_items.map(item => item && mapStats(item)).filter(i => i);
  const combined_stats = equipped_stats.length > 0 && equipped_stats.reduce((acc, cur) => combineStats(acc, cur));
  return <><Divider style={{marginTop: 0}}>Stats</Divider>
    <div className="content"><StatsDisplay stats={combined_stats}/></div>
  </>;
};

StatsSummary.propTypes = {equipped: PropTypes.object};

export class CharacterSummary extends Component {
  render() {
    return <>
      <EquipmentSummary equipped={this.props.equipped} character={this.props.character}
                        handleSelectItem={this.props.handleSelectItem}/>
      <StatsSummary equipped={this.props.equipped}/>
    </>;
  }
}

CharacterSummary.propTypes = {
  equipped: PropTypes.object,
  character: PropTypes.any,
  handleSelectItem: PropTypes.func,
};
